import { t } from "i18next";
import DBFieldBlock, { FieldHint } from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import DBSwitch from "../../component/global/DBSwitch";
import DBTextArea from "../../component/global/DBTextArea";
import {
    defaultPhoneSettings,
    phoneDialOptions,
    phoneInterviewOptions,
} from "./constants";

export default function PhoneSettingsFields({
    value,
    onChange,
    disabled = false,
    compact = false,
}) {
    const settings = { ...defaultPhoneSettings(), ...(value || {}) };
    const patch = (next) => onChange({ ...settings, ...next });

    return (
        <div className="phone-settings-fields">
            {!compact && (
                <h4 className="phone-settings-title">
                    {t("survey.phone.settings")}
                </h4>
            )}
            <div className="survey-form-grid">
                <DBFieldBlock
                    title={t("survey.phone.dial")}
                    hint={t("survey.phone.dial.hint")}
                >
                    <DBSelect
                        size="large"
                        disabled={disabled}
                        value={settings.dial_mode}
                        onChange={(dial_mode) => patch({ dial_mode })}
                        options={phoneDialOptions()}
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("survey.phone.interview")}
                    hint={t("survey.phone.interview.hint")}
                >
                    <DBSelect
                        size="large"
                        disabled={disabled}
                        value={settings.interview_mode}
                        onChange={(interview_mode) =>
                            patch({ interview_mode })
                        }
                        options={phoneInterviewOptions()}
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("survey.phone.max_attempts")}
                    hint={t("survey.phone.max_attempts.hint")}
                >
                    <DBInput
                        size="large"
                        type="number"
                        min={1}
                        max={20}
                        disabled={disabled}
                        value={settings.max_attempts}
                        onChange={(e) =>
                            patch({
                                max_attempts: Number(e.target.value) || 1,
                            })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("survey.phone.retry_minutes")}
                    hint={t("survey.phone.retry_minutes.hint")}
                >
                    <DBInput
                        size="large"
                        type="number"
                        min={1}
                        disabled={disabled}
                        value={settings.retry_interval_minutes}
                        onChange={(e) =>
                            patch({
                                retry_interval_minutes:
                                    Number(e.target.value) || 1,
                            })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("survey.phone.window.start")}
                    hint={t("survey.phone.window.start.hint")}
                >
                    <DBInput
                        size="large"
                        type="time"
                        disabled={disabled}
                        value={String(settings.calling_window_start || "").slice(
                            0,
                            5
                        )}
                        onChange={(e) =>
                            patch({ calling_window_start: e.target.value })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("survey.phone.window.end")}
                    hint={t("survey.phone.window.end.hint")}
                >
                    <DBInput
                        size="large"
                        type="time"
                        disabled={disabled}
                        value={String(settings.calling_window_end || "").slice(
                            0,
                            5
                        )}
                        onChange={(e) =>
                            patch({ calling_window_end: e.target.value })
                        }
                    />
                </DBFieldBlock>
                {!compact && (
                    <>
                        <DBFieldBlock
                            title={t("survey.phone.timeout")}
                            hint={t("survey.phone.timeout.hint")}
                        >
                            <DBInput
                                size="large"
                                type="number"
                                min={3}
                                disabled={disabled}
                                value={settings.timeout_seconds}
                                onChange={(e) =>
                                    patch({
                                        timeout_seconds:
                                            Number(e.target.value) || 8,
                                    })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock
                            title={t("survey.phone.invalid_retries")}
                            hint={t("survey.phone.invalid_retries.hint")}
                        >
                            <DBInput
                                size="large"
                                type="number"
                                min={0}
                                max={10}
                                disabled={disabled}
                                value={settings.max_invalid_retries}
                                onChange={(e) =>
                                    patch({
                                        max_invalid_retries:
                                            Number(e.target.value) || 0,
                                    })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock
                            title={t("survey.phone.opt_out")}
                            hint={t("survey.phone.opt_out.hint")}
                        >
                            <DBInput
                                size="large"
                                maxLength={1}
                                disabled={disabled}
                                value={settings.opt_out_digit}
                                onChange={(e) =>
                                    patch({
                                        opt_out_digit: e.target.value.slice(
                                            0,
                                            1
                                        ),
                                    })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock
                            title={t("survey.phone.greeting")}
                            hint={t("survey.phone.greeting.hint")}
                            span={2}
                        >
                            <DBTextArea
                                rows={2}
                                disabled={disabled}
                                value={settings.greeting_text || ""}
                                onChange={(e) =>
                                    patch({ greeting_text: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock
                            title={t("survey.phone.goodbye")}
                            hint={t("survey.phone.goodbye.hint")}
                            span={2}
                        >
                            <DBTextArea
                                rows={2}
                                disabled={disabled}
                                value={settings.goodbye_text || ""}
                                onChange={(e) =>
                                    patch({ goodbye_text: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                    </>
                )}
            </div>
            <label className="phone-settings-toggle">
                <DBSwitch
                    checked={Boolean(settings.record_calls)}
                    disabled={disabled}
                    onChange={(record_calls) => patch({ record_calls })}
                />
                <span>{t("survey.phone.record_calls")}</span>
                <FieldHint hint={t("survey.phone.record_calls.hint")} />
            </label>
        </div>
    );
}
